import React from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";
import { VaultSidebar, VaultDocItem } from "@/components/digibox/VaultSidebar";
import { useDownloadTracker } from "@/hooks/useDownloadTracker";
import { renderMentionsVault as renderMentions } from "@/utils/mentions";
import { timeAgo } from "@/utils/timeAgo";

function mapStatus(
  acceptanceStatus?: string
): VaultDocItem["status"] {
  if (!acceptanceStatus || acceptanceStatus === "Pending") return "action";
  if (acceptanceStatus === "Accepted") return "accepted";
  if (acceptanceStatus === "Read") return "read";
  return "pending";
}

export const DigiBoxHub: React.FC = () => {
  const navigate = useNavigate();

  // coreBOS portal session handles data scoping automatically
  const { data: acceptanceData, isLoading: acceptanceLoading } = useList({
    resource: "acceptance",
    pagination: { pageSize: 100 },
  });
  const acceptances = (acceptanceData?.data || []) as any[];

  const { data: policiesData, isLoading: policiesLoading } = useList({
    resource: "policies",
    pagination: { pageSize: 50 },
    sorters: [{ field: "title", order: "asc" }],
  });

  const { data: contentData } = useList({
    resource: "content",
    pagination: { pageSize: 50 },
  });

  const { data: documentsData } = useList({
    resource: "documents",
    pagination: { pageSize: 1 },
  });

  const { data: commentsData } = useList({
    resource: "comments",
    pagination: { pageSize: 10 },
    sorters: [{ field: "createdtime", order: "desc" }],
  });
  const recentComments = (commentsData?.data || []) as any[];

  const policies = (policiesData?.data || []) as any[];
  const contents = (contentData?.data || []) as any[];
  const totalDocuments = documentsData?.total || 0;

  // Active policies = "Informative da Firmare"
  const activePolicies = policies.filter(
    (p) => p.policystatus === "Active"
  );
  const activePoliciesCount = activePolicies.length;

  // Pending content = "Contenuti da Visionare" (all cbContent records)
  const pendingContentCount = contents.length;

  // Build sidebar documents from policies
  const documents: VaultDocItem[] = policies.map((p: any) => {
    const relatedAcceptance = acceptances.find(
      (a: any) => a.acctopolicy === p.id
    );
    return {
      id: p.id,
      category: p.policy_type || "Policy",
      title: p.title || "Untitled",
      date: p.createdtime
        ? new Date(p.createdtime).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        : "—",
      status: relatedAcceptance
        ? mapStatus(relatedAcceptance.acceptance_status)
        : p.policystatus === "Active"
          ? "action"
          : "read",
    };
  });

  const handleSelect = (id: string) => {
    navigate(`/digibox/policies/${id}`);
  };

  // Build lookup maps from already-loaded data for resolving references
  const policyById = new Map(policies.map((p: any) => [p.id, p.title || "Policy"]));
  const contentById = new Map(contents.map((c: any) => [c.id, c.title || "Contenuto"]));

  // Resolve a related_to WSID to a name + path
  const resolveTarget = (id?: string): { name: string; path: string } | null => {
    if (!id) return null;
    if (id.startsWith("73x") && policyById.has(id)) return { name: policyById.get(id)!, path: `/digibox/policies/${id}` };
    if (id.startsWith("85x") && contentById.has(id)) return { name: contentById.get(id)!, path: `/digibox/content` };
    return null;
  };

  // Resolve the portal contact name for "actor"
  const portalName = (() => {
    try { return JSON.parse(localStorage.getItem("portal_session") || "{}").contactName || null; } catch { return null; }
  })();

  // Download tracker
  const { downloads } = useDownloadTracker();

  // Build activity timeline
  type TimelineEntry = {
    id: string;
    type: "comment" | "accept" | "read" | "download";
    actor: string;
    body: string;
    target?: { name: string; path: string };
    time: string;
  };
  const timelineEntries: TimelineEntry[] = [];

  for (const c of recentComments) {
    const target = resolveTarget(c.related_to);
    timelineEntries.push({
      id: `c-${c.id}`,
      type: "comment",
      actor: portalName || "Tu",
      body: (c.commentcontent || "").slice(0, 160),
      target: target || undefined,
      time: c.createdtime || "",
    });
  }

  for (const a of acceptances) {
    const status = a.acceptance_status;
    if (!status || status === "Pending") continue;
    const target = resolveTarget(a.acctopolicy) || resolveTarget(a.acceptancetocontent);
    timelineEntries.push({
      id: `a-${a.id}`,
      type: status === "Read" ? "read" : "accept",
      actor: a.acctitle || portalName || "Tu",
      body: "",
      target: target || undefined,
      time: a.acclastdate || a.modifiedtime || "",
    });
  }

  // Download events from KV store
  for (const [docId, dl] of Object.entries(downloads)) {
    timelineEntries.push({
      id: `dl-${docId}`,
      type: "download",
      actor: portalName || "Tu",
      body: "",
      target: { name: dl.filename || docId, path: "/digibox/archive" },
      time: dl.downloadedAt || "",
    });
  }

  timelineEntries.sort((a, b) => (b.time || "").localeCompare(a.time || ""));
  const topEntries = timelineEntries.slice(0, 8);

  const dotClass: Record<string, string> = {
    comment: "vault-timeline-dot-comment",
    accept: "vault-timeline-dot-accept",
    read: "vault-timeline-dot-read",
    download: "vault-timeline-dot-download",
  };
  const dotIcon: Record<string, string> = { comment: "✉", accept: "✓", read: "●", download: "↓" };
  const actionLabel: Record<string, string> = { comment: "ha commentato su", accept: "ha accettato", read: "ha letto", download: "ha scaricato" };

  return (
    <>
      <VaultSidebar
        documents={documents}
        onSelect={handleSelect}
        loading={policiesLoading || acceptanceLoading}
      />

      <main className="vault-panel vault-main-content">
        <div className="vault-hub-content">
          {/* Hero + stat cards in a 2-column layout */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--vault-spacing-md)", alignItems: "start" }}>
            <div className="vault-hub-hero" style={{ paddingBottom: 0, borderBottom: "none" }}>
              <h1 className="vault-heading-display">
                DOCUMENT
                <br />
                VAULT
              </h1>
            </div>

            {/* Compact stat cards stacked on the right */}
            <div style={{ display: "flex", flexDirection: "column", gap: "1px", background: "var(--vault-grid-line)" }}>
              <div
                className="vault-hub-stat vault-hub-stat-clickable"
                onClick={() => navigate("/digibox/policies")}
                style={{ borderLeft: "3px solid var(--vault-zone-pending-border)", padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span className="vault-label-tiny">Informative da Firmare</span>
                  <span className="vault-hub-stat-value" style={{ fontSize: "var(--vault-text-h3)" }}>{activePoliciesCount}</span>
                </div>
              </div>
              <div
                className="vault-hub-stat vault-hub-stat-clickable"
                onClick={() => navigate("/digibox/content")}
                style={{ borderLeft: "3px solid var(--vault-zone-info-border)", padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span className="vault-label-tiny">Contenuti da Visionare</span>
                  <span className="vault-hub-stat-value" style={{ fontSize: "var(--vault-text-h3)" }}>{pendingContentCount}</span>
                </div>
              </div>
              <div
                className="vault-hub-stat vault-hub-stat-clickable"
                onClick={() => navigate("/digibox/archive")}
                style={{ borderLeft: "3px solid var(--vault-zone-download-border)", padding: "var(--vault-spacing-xs) var(--vault-spacing-sm)" }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <span className="vault-label-tiny">Archivio Documenti</span>
                  <span className="vault-hub-stat-value" style={{ fontSize: "var(--vault-text-h3)" }}>{totalDocuments}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Activity timeline */}
          {topEntries.length > 0 && (
            <div style={{ borderTop: "1px solid var(--vault-grid-line)", paddingTop: "var(--vault-spacing-sm)" }}>
              <span className="vault-label-bracketed" style={{ display: "block", marginBottom: "var(--vault-spacing-sm)" }}>
                Attività Recente
              </span>
              <div className="vault-timeline">
                {topEntries.map((entry) => (
                  <div key={entry.id} className="vault-timeline-item">
                    <div className={`vault-timeline-dot ${dotClass[entry.type] || ""}`}>
                      {dotIcon[entry.type]}
                    </div>
                    <div className="vault-timeline-content">
                      <div className="vault-timeline-header">
                        <span className="vault-label-tiny">
                          <strong>{entry.actor}</strong>{" "}
                          {actionLabel[entry.type]}{" "}
                          {entry.target ? (
                            <span
                              className="vault-timeline-target"
                              onClick={() => navigate(entry.target!.path)}
                            >
                              {entry.target.name}
                            </span>
                          ) : null}
                        </span>
                        <span className="vault-label-tiny" style={{ flexShrink: 0 }}>
                          {timeAgo(entry.time)}
                        </span>
                      </div>
                      {entry.type === "comment" && entry.body && (
                        <div className="vault-timeline-body">{renderMentions(entry.body)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {timelineEntries.length > topEntries.length && (
                <div style={{ marginTop: "var(--vault-spacing-sm)" }}>
                  <span
                    className="vault-timeline-target"
                    onClick={() => navigate("/digibox/timeline")}
                    style={{ fontSize: "var(--vault-text-small)" }}
                  >
                    Vedi tutta l'attività ({timelineEntries.length} eventi) →
                  </span>
                </div>
              )}
            </div>
          )}

          {topEntries.length === 0 && (
            <div>
              <p className="vault-text-body-editorial" style={{ color: "var(--vault-fg-muted)" }}>
                Seleziona un documento dalla barra laterale oppure clicca su una
                delle sezioni sopra per accedere ai contenuti.
              </p>
            </div>
          )}
        </div>
      </main>
    </>
  );
};
